// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
// Licensed under the Apache License, Version 2.0 (https://www.apache.org/licenses/LICENSE-2.0).
// Blueprint_Set membership rules (blueprint-sets Req 2, 3.2, 3.6; design "sets/membership.ts").
// Pure: the API resolves Members from the catalog, the web app from the set detail response, and both run the same
// rules so the messages match.
import { canonicalJson } from '../preview.js';
import { BLUEPRINT_NAME_PATTERN, MAX_DESCRIPTION } from '../schema/validate.js';
/** Assumption 1: trim, collapse whitespace, lower-case. */
export const normalizeDocType = (s) => s.trim().replace(/\s+/g, ' ').toLowerCase();
export const MAX_TYPE_SLUG = 40;
/** BDA allows at most 40 document blueprints in one project, so no set can have more Members. */
export const MAX_SET_MEMBERS_LIMIT = 40;
/** Readable, BDA-name-safe form of a document type: [a-z0-9-], at most 40 characters, 'document' when empty. */
export function typeSlug(documentType) {
    const slug = normalizeDocType(documentType)
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, MAX_TYPE_SLUG)
        .replace(/-+$/, '');
    return slug || 'document';
}
/** Below this many characters a Member's description gets a non-blocking warning (Req 2.5). */
export const MIN_MATCH_DESCRIPTION = 40;
export const NAME_MESSAGE = 'Name must be 1-128 characters: letters, digits, spaces, hyphens, or underscores.';
export const noDescriptionMessage = (name) => `BDA uses the description to match documents to blueprints. Add a description to ${name}.`;
export const sameTypeMessage = (a, b, type) => `${a} and ${b} are both '${type}'. A set can have one blueprint per document type.`;
/**
 * Validates a set's name, description, and Members (design "sets/membership.ts"), rules in order: name and
 * description; 1 to `maxMembers` Members; no blueprint twice; each Member exists, is owned, and is not archived (unless
 * `allowArchived`); a non-blank description; no two equal document types; with `forPublish`, no `DRAFT` ref. Each key
 * keeps the first message that applies. A Member that is missing, not owned, or a repeat is not checked further.
 */
export function validateSetDefinition(def, members, 
/** `allowArchived`: runs of a Set_Version, which stay runnable when a pinned Member is archived (Req 3.4). */
opts) {
    const errors = {};
    const put = (key, message) => {
        if (!(key in errors))
            errors[key] = message;
    };
    if (!BLUEPRINT_NAME_PATTERN.test(def.name) || def.name.trim().length === 0)
        put('name', NAME_MESSAGE);
    if (def.description.length > MAX_DESCRIPTION)
        put('description', `Description must be at most ${MAX_DESCRIPTION} characters.`);
    if (members.length === 0)
        put('members', 'Add at least one blueprint to the set.');
    else if (members.length > opts.maxMembers)
        put('members', `A set can have at most ${opts.maxMembers} blueprints (${members.length} now).`);
    const seen = new Set();
    /** Members that passed the identity checks and take part in the type and warning checks. */
    const checked = [];
    members.forEach((m, i) => {
        const key = `members.${i}`;
        if (seen.has(m.blueprintId)) {
            put(key, `${m.name || 'This blueprint'} is in the set more than once.`);
            return;
        }
        seen.add(m.blueprintId);
        if (!m.exists) {
            put(key, m.ref === 'DRAFT' ? 'This blueprint no longer exists.' : `Version ${m.ref} of this blueprint does not exist.`);
            return;
        }
        if (!m.owned) {
            put(key, 'A set can only use your own blueprints.');
            return;
        }
        if (m.archived && !opts.allowArchived)
            put(key, `${m.name} is archived. Remove it from the set.`);
        if (m.description.trim().length === 0)
            put(key, noDescriptionMessage(m.name));
        checked.push(i);
    });
    for (let x = 0; x < checked.length; x++) {
        const j = checked[x];
        const type = normalizeDocType(members[j].documentType);
        for (let y = 0; y < x; y++) {
            const i = checked[y];
            if (normalizeDocType(members[i].documentType) === type) {
                put(`members.${j}.documentType`, sameTypeMessage(members[i].name, members[j].name, type));
                break;
            }
        }
    }
    if (opts.forPublish) {
        checked.forEach((i) => {
            const m = members[i];
            if (m.ref === 'DRAFT')
                put(`members.${i}`, `Publish ${m.name} first, or pick one of its versions. A set version uses published blueprint versions only.`);
        });
    }
    const warnings = [];
    for (const i of checked) {
        const m = members[i];
        const len = m.description.trim().length;
        if (len > 0 && len < MIN_MATCH_DESCRIPTION) {
            warnings.push({
                members: [i],
                kind: 'SHORT_DESCRIPTION',
                message: `${m.name} has a short description. BDA matches documents more reliably when the description says what the document is (at least ${MIN_MATCH_DESCRIPTION} characters).`,
            });
        }
    }
    for (let x = 0; x < checked.length; x++) {
        for (let y = x + 1; y < checked.length; y++) {
            const a = members[checked[x]];
            const b = members[checked[y]];
            const da = normalizeDocType(a.description);
            if (da.length > 0 && da === normalizeDocType(b.description)) {
                warnings.push({
                    members: [checked[x], checked[y]],
                    kind: 'SAME_DESCRIPTION',
                    message: `${a.name} and ${b.name} have the same description, so BDA cannot tell them apart by it.`,
                });
            }
            if (normalizeDocType(a.documentType) !== normalizeDocType(b.documentType) &&
                typeSlug(a.documentType) === typeSlug(b.documentType)) {
                warnings.push({
                    members: [checked[x], checked[y]],
                    kind: 'SAME_SLUG',
                    message: `${a.name} and ${b.name} have document types that look the same in BDA names ('${typeSlug(a.documentType)}').`,
                });
            }
        }
    }
    return { errors, warnings };
}
/** Req 3.6: Members added, removed, or with a changed ref (by blueprintId), and whether Summary_Rules changed. */
export function diffSetVersions(a, b) {
    const inA = new Map(a.members.map((m) => [m.blueprintId, m]));
    const inB = new Map(b.members.map((m) => [m.blueprintId, m]));
    return {
        added: b.members.filter((m) => !inA.has(m.blueprintId)),
        removed: a.members.filter((m) => !inB.has(m.blueprintId)),
        changed: a.members.flatMap((m) => {
            const other = inB.get(m.blueprintId);
            return other && other.ref !== m.ref ? [{ blueprintId: m.blueprintId, from: m.ref, to: other.ref }] : [];
        }),
        rulesChanged: canonicalJson(a.summaryRules) !== canonicalJson(b.summaryRules),
    };
}
//# sourceMappingURL=membership.js.map